<template>
  <div
    ref="filterItem"
    v-scroll.self="hideDateRange"
    :class="[$.filter_item, isDate ? 'filter_item_date' : '']"
  >
    <div
      v-if="filterItem.template === 'input'"
      :class="[$.input, readonly ? $.input_without_padding : '']"
      @click="clickDateInput"
    >
      <vgp-input
        ref="input"
        v-model="inputValue"
        :placeholder="filterItem.placeholder"
        :readonly="readonly"
        :disabled-input-only="isDate"
        :value="inputValue"
        :small="true"
        :icons-left="textInputIconsLeft"
        :icons-right="textInputIconsRight"
        @input="onInput"
      />
    </div>

    <vgp-date-range-picker
      v-click-outside="clickOutsideDateRange"
      :is-range="true"
      :show="isShowDateRange"
      :range-data="rangeValue"
      :target-element="dateRangeTarget"
      :presets-type="presetsTypeByFilterType"
      @onChange="changeDateRange"
      @onClose="hideDateRange"
    />

    <div v-if="isMultiInputItem && readonly">
      <div
        :class="[$.input, $.input_without_padding]"
        :style="{marginBottom: '8px'}"
      >
        <div
          :class="$.readonly_text"
          v-html="`${multiInputValues.start.value} &#8212; ${multiInputValues.end.value}`"
        />
      </div>
    </div>
    <div v-else-if="isMultiInputItem">
      <div
        :class="[$.input, readonly ? $.input_without_padding : '']"
        :style="{marginBottom: '8px'}"
      >
        <vgp-input
          ref="input"
          v-model="multiInputValues.start.value"
          :placeholder="locales('filter_number_type_custom_column_from')"
          :readonly="readonly"
          :value="multiInputValues.start.value"
          :small="true"
          :icons-left="textInputIconsLeft"
          :input-type="isCustomNumberProp ? 'number' : 'text'"
          @onChange="onChangeMultiInput(multiInputValues.start.prop, $event.target.value)"
        />
      </div>
      <div :class="[$.input, readonly ? $.input_without_padding : '']">
        <vgp-input
          ref="input"
          v-model="multiInputValues.end.value"
          :placeholder="locales('filter_number_type_custom_column_to')"
          :readonly="readonly"
          :value="multiInputValues.end.value"
          :small="true"
          :icons-left="textInputIconsLeft"
          :input-type="isCustomNumberProp ? 'number' : 'text'"
          @onChange="onChangeMultiInput(multiInputValues.end.prop, $event.target.value)"
        />
      </div>
    </div>

    <div
      v-if="filterItem.template === 'dropdown'"
      :class="$.dropdown"
    >
      <dropdown-select
        :readonly="readonly"
        :drop-list="filterItem.options"
        :use-color-palette="isTaskColorProp"
        :placeholder="placeholder"
        :multiselect="hasMultiselect"
        :use-search="hasSearch"
        :use-select-all="hasSelectAll"
        :show-selected-first="false"
        :show-reset-btn="true"
        :highlight-active-state="false"
        :item-template="chipTemplate"
        :track-by-text="'value'"
        :drop-position="'fixed'"
        group-by="group"
        track-by-icon-name="icon"
        track-by-bg-color="color"
        :outline="readonly ? 'none' : 'always'"
        :popup-max-height="200"
        :selected="dropdownValue"
        @change="changeOptions"
      />
    </div>

    <div
      v-if="filterItem.template === 'switch'"
      :class="$.toggle"
    >
      <div :class="[$.label, readonly ? $.label__readonly : '']">
        {{ readonly ? locales('common_yes') : filterItem.label }}
      </div>
      <square-toggle
        v-if="!readonly"
        :is-toggle-on="switchValue"
        @onChange="onChangeToggle(!filterItem.value)"
      />
    </div>
  </div>
</template>

<script>
import constants from '../../../helpers/constants';
import filterConstants from '../constants';
import dropdownSelect from '../../common/VueComponents/dropdownSelect/dropdownSelect.vue';
import vgpInput from '../../common/VueComponents/vInput/vgpInput.vue';
import squareToggle from '../../common/VueComponents/checkbox/squareToggle.vue';
import vgpDateRangePicker from '../../common/VueComponents/dateRangePicker/vgpDateRange.vue';
import moment from '../../../libs/moment';
import timeParser from '../../../helpers/timeParser';

const DATE_INPUTS = [
  filterConstants.PROPERTY.rangeDate,
  filterConstants.PROPERTY.startDate,
  filterConstants.PROPERTY.endDate,
  filterConstants.PROPERTY.creationDate,
  filterConstants.PROPERTY.date,
];

const RESOURCES_OPTIONS = [
  filterConstants.PROPERTY.assignee,
  filterConstants.PROPERTY.resource_id,
  filterConstants.PROPERTY.reporter,
  filterConstants.PROPERTY.owner_id,
];

const PROJECT_OPTIONS = [
  filterConstants.PROPERTY.gantt_id,
  filterConstants.PROPERTY.portfolio_id,
];

export default {
  name: 'FilterItem',
  components: {
    dropdownSelect, vgpInput, squareToggle, vgpDateRangePicker,
  },
  props: {
    filterItem: { type: Object, required: true, default: () => {} },
    readonly: { type: Boolean, required: false, default: false },
  },
  emits: {
    changeFilter: Object,
    showDateRangePicker: Object,
  },
  data() {
    return {
      locales: __,
      inputValue: '',
      dropdownValue: [],
      switchValue: false,
      rangeValue: null,
      timeValue: { from: '', to: '' },
      customNumberValue: { start: '', end: '' },
      dateRangeTarget: null,
      isShowDateRange: false,
      isRangeHasBeenReset: false,
    };
  },
  computed: {
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    currentFilterAdvancedSettings() {
      if (this.currentFilter?.type) {
        return this.$store.getters['filter/getAdvancedSettingsByFilterType'](this.currentFilter.type);
      }

      return null;
    },
    isTimeProp() {
      return filterConstants.PROPERTY.time === this.filterItem.property;
    },
    isCustomNumberProp() {
      return constants.CUSTOM_COLUMNS_TYPES.number.id === this.filterItem.typeId;
    },
    isMultiInputItem() {
      return this.isTimeProp || this.isCustomNumberProp;
    },
    multiInputValues() {
      if (this.isTimeProp) {
        return {
          start: {
            value: this.timeValue.from || (this.readonly ? 0 : ''),
            prop: 'from',
          },
          end: {
            value: this.timeValue.to || (this.readonly ? '&#8734;' : ''),
            prop: 'to',
          },
        };
      }

      if (this.isCustomNumberProp) {
        return {
          start: {
            value: this.customNumberValue.start || (this.readonly ? 0 : ''),
            prop: 'start',
          },
          end: {
            value: this.customNumberValue.end || (this.readonly ? '&#8734;' : ''),
            prop: 'end',
          },
        };
      }

      return {
        start: {
          value: 0,
          prop: 'start',
        },
        end: {
          value: 0,
          prop: 'end',
        },
      };
    },
    isTaskColorProp() {
      return filterConstants.PROPERTY.color === this.filterItem.property;
    },
    isColor() {
      const isCustomColor = constants.CUSTOM_COLUMNS_TYPES.color.id === this.filterItem.typeId;

      return this.isTaskColorProp || isCustomColor;
    },
    isDate() {
      const isStaticFilterItemWithDate = DATE_INPUTS.includes(this.filterItem.property);
      const isCustomColumnsItemWithDate = [constants.CUSTOM_COLUMNS_TYPES.date.id].includes(this.filterItem.typeId);

      return isStaticFilterItemWithDate || isCustomColumnsItemWithDate;
    },
    isResources() {
      const isStaticItemWithResourcesOptions = RESOURCES_OPTIONS.includes(this.filterItem.property);
      const isCustomColumnItemWithResourcesOptions = [constants.CUSTOM_COLUMNS_TYPES.resources.id].includes(this.filterItem.typeId);

      return isStaticItemWithResourcesOptions || isCustomColumnItemWithResourcesOptions;
    },
    textInputIconsLeft() {
      if (!this.readonly && this.isCustomNumberProp) {
        return [];
      }

      if (!this.readonly && this.isTimeProp) {
        return [{ name: 'time', handler: null }];
      }

      if (!this.readonly && !this.isDate) {
        return [{ name: 'search', handler: null }];
      }

      return [];
    },
    textInputIconsRight() {
      let icons = [];

      if (this.inputValue !== '' && !this.readonly) {
        icons = [{ name: 'close-5', type: 'bold', handler: this.resetInputValue }];
      }

      if (!this.readonly && this.isDate) {
        icons.push({
          name: 'calendar', type: 'regular', size: 24, handler: this.showDateRangePicker,
        });
      }

      return icons;
    },
    chipTemplate() {
      if ([
        filterConstants.PROPERTY.priority,
        filterConstants.PROPERTY.status,
        filterConstants.PROPERTY.type,
      ].includes(this.filterItem.property)) {
        return 'withIcon';
      }

      if ([constants.CUSTOM_COLUMNS_TYPES.tags.id].includes(this.filterItem.typeId)) {
        return 'label';
      }

      if (this.isResources) {
        return 'user';
      }

      if (this.isColor) {
        return 'color';
      }

      return 'text';
    },
    hasSelectAll() {
      const customColumnsOptionsWithSelectAll = [
        constants.CUSTOM_COLUMNS_TYPES.tags.id,
        constants.CUSTOM_COLUMNS_TYPES.resources.id,
        constants.CUSTOM_COLUMNS_TYPES.select.id,
        constants.CUSTOM_COLUMNS_TYPES.multiselect.id,
      ];

      return RESOURCES_OPTIONS.includes(this.filterItem.property)
      || customColumnsOptionsWithSelectAll.includes(this.filterItem.typeId)
      || this.filterItem.property === filterConstants.PROPERTY.gantt_id;
    },
    hasMultiselect() {
      const filtersWithoutMultiselect = [
        filterConstants.FILTERS.reportsProgressMonth,
        filterConstants.FILTERS.reportsBudgetMonth,
        filterConstants.FILTERS.reportsTimeMonth,
      ];
      const portfolio = this.filterItem.property === filterConstants.PROPERTY.portfolio_id;
      const ganttIds = this.filterItem.property === filterConstants.PROPERTY.gantt_id;
      const checkboxCustom = this.filterItem?.typeId === constants.CUSTOM_COLUMNS_TYPES.checkbox.id;
      const onlyOneSelect = ganttIds && filtersWithoutMultiselect.includes(this.currentFilter?.type);

      if (ganttIds && this.currentFilterAdvancedSettings?.baselineEnabled) {
        return false;
      }

      if (portfolio || checkboxCustom || onlyOneSelect) {
        return false;
      }

      return true;
    },
    presetsTypeByFilterType() {
      const filters = [
        filterConstants.FILTERS.reportsProgressMonth,
        filterConstants.FILTERS.reportsBudgetMonth,
        filterConstants.FILTERS.reportsTimeMonth,
      ];

      if (filters.includes(this.currentFilter?.type)) {
        return 'year';
      }

      return 'month';
    },
    hasSearch() {
      const customColumnsOptionsWithSearch = [
        constants.CUSTOM_COLUMNS_TYPES.multiselect.id,
        constants.CUSTOM_COLUMNS_TYPES.tags.id,
        constants.CUSTOM_COLUMNS_TYPES.select.id,
        constants.CUSTOM_COLUMNS_TYPES.resources.id,
      ];

      return RESOURCES_OPTIONS.includes(this.filterItem.property)
        || customColumnsOptionsWithSearch.includes(this.filterItem.typeId)
        || this.filterItem.property === filterConstants.PROPERTY.reporter
        || PROJECT_OPTIONS.includes(this.filterItem.property);
    },
    placeholder() {
      if (!this.hasMultiselect && this.filterItem.placeholderSingle) {
        return this.filterItem.placeholderSingle;
      }

      return this.filterItem.placeholder;
    },
  },
  watch: {
    filterItem: {
      handler() {
        this.setValues();
      },
      immediate: true,
    },
  },
  created() {
    this.setValues();
  },
  methods: {
    setValues() {
      if (this.filterItem) {
        const value = this.filterItem.value;

        if (!value || value === '') {
          this.inputValue = '';
          this.switchValue = false;
          this.dropdownValue = [];
          this.rangeValue = null;
          this.timeValue = { from: '', to: '' };
          this.customNumberValue = { start: '', end: '' };

          return;
        }

        if (this.isDate && value !== '') {
          const startValue = moment(value?.start).format(user.dateFormat);
          const endValue = moment(value?.end).format(user.dateFormat);

          this.rangeValue = value;
          this.inputValue = `${startValue} - ${endValue}`;

          return;
        }

        if (this.isTaskColorProp) {
          this.dropdownValue = value;

          return;
        }

        if (this.filterItem.options && this.filterItem.options.length) {
          this.dropdownValue = this.filterItem.options?.filter(opt => value.includes(opt.id));

          return;
        }

        if (this.isTimeProp && value !== '') {
          this.timeValue = {
            from: timeParser.output(value.from),
            to: timeParser.output(value.to),
          };
        }

        if (this.isCustomNumberProp && value !== '') {
          this.customNumberValue = {
            start: value.start,
            end: value.end,
          };
        }

        this.inputValue = value;
        this.switchValue = value;
      }
    },
    showDateRangePicker() {
      if (this.isShowDateRange) {
        this.hideDateRange();
      } else {
        const input = this.$refs.input;

        this.dateRangeTarget = this.$refs.filterItem;
        this.isShowDateRange = true;

        input.isActive = true;
      }
    },
    hideDateRange() {
      const input = this.$refs.input;

      this.isShowDateRange = false;
      this.dateRangeTarget = null;

      if (input) {
        input.isActive = false;
      }
    },
    clickOutsideDateRange(e) {
      if (e.target.closest('filter_item_date')) {
        return;
      }

      if (this.isShowDateRange) {
        this.hideDateRange();
      }
    },
    resetInputValue() {
      this.inputValue = '';

      if (this.isDate) {
        this.isRangeHasBeenReset = true;
        this.rangeValue = null;
      }

      const changes = {
        [this.filterItem.property]: '',
      };

      this.$emit('changeFilter', changes);
    },
    changeDateRange(range) {
      if (this.isRangeHasBeenReset) {
        this.isRangeHasBeenReset = false;

        return;
      }

      if (this.isDate) {
        const changes = {
          [this.filterItem.property]: range,
        };

        this.$emit('changeFilter', changes);
      }
    },
    changeOptions(options) {
      this.selected = options;
      const filterValues = options.map(i => i.id);
      const changes = {
        [this.filterItem.property]: filterValues.length ? filterValues : '',
      };

      this.$emit('changeFilter', changes);
    },
    onInput(value) {
      this.inputValue = value;

      const changes = {
        [this.filterItem.property]: value,
      };

      this.$emit('changeFilter', changes);
    },
    onChangeToggle(value) {
      const changes = {
        [this.filterItem.property]: value,
      };

      this.$emit('changeFilter', changes);
    },
    clickDateInput() {
      if (this.isDate && !this.readonly) {
        this.showDateRangePicker();
      }
    },
    onChangeMultiInput(prop, val) {
      if (this.isTimeProp) {
        const valid = timeParser.input(val).value;

        if (!valid) {
          this.timeValue = {
            ...this.timeValue,
            [prop]: '',
          };

          return;
        }

        const time = {
          ...this.timeValue,
          [prop]: valid,
        };

        const changes = {
          [this.filterItem.property]: time,
        };

        this.$emit('changeFilter', changes);
      }

      if (this.isCustomNumberProp) {
        const numbers = {
          ...this.customNumberValue,
          [prop]: val,
        };
        const isEmpty = _.values(numbers).every(item => !item);

        const changes = {
          [this.filterItem.property]: isEmpty ? '' : numbers,
        };

        this.$emit('changeFilter', changes);
      }
    },
  },
};
</script>

<style module='$' lang='less'>
@import '../../../less/_mixins.less';
@import '../../../less/_fonts.less';

.filter_item{
  .input_without_padding {
    & > div {
      padding: 0;
    }
  }
  .toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .label {
      .FontLatoBold;
      .fs(14px, 24px);
      color: #999999;

      &__readonly {
        .FontLatoRegular;
        .fs(14px, 20px);
        color: #191919;
      }
    }
  }

  .readonly_text {
    .FontLatoRegular;
    .fs(14px, 20px);
    color: #191919;
  }
}
</style>
